import { CampusClient } from "./campusClient"

export enum CourseRegistrationType {
  Program = "Program",
  Manual = "Manual",
}

export enum CourseRegistrationEndStatus {
  Completed = "Completed",
  Failed = "Failed",
  Removed = "Removed",
}

export type CourseRegistrationEndReason =
  | { status: CourseRegistrationEndStatus.Completed }
  | { status: CourseRegistrationEndStatus.Failed }
  | { status: CourseRegistrationEndStatus.Removed; removedBy: string }

export class CourseClassRegistrationClient {
  static async registerStudentToClassManually(
    courseClassId: string,
    studentId: string,
    startDate: Date,
    userClientId: string,
  ): Promise<void> {
    return CampusClient.post<void>(
      `courseRegistration/register-student-to-class`,
      {
        courseClassId,
        userClientId: studentId,
        startDate,
        insertionType: CourseRegistrationType.Manual,
        insertManually: {
          type: CourseRegistrationType.Manual,
          userId: userClientId,
        },
      },
    )
  }

  static async unregisterStudentFromClass(
    courseClassId: string,
    studentId: string,
    endDate: Date,
    endReason: CourseRegistrationEndReason,
  ) {
    return CampusClient.delete<void>(
      `courseRegistration/unregister-student-from-class`,
      {
        courseClassId,
        userClientId: studentId,
        endDate,
        ...endReasonToRequest(endReason),
      },
    )
  }

  static async dummyUnregisterStudentFromClass(
    courseClassId: string,
    studentId: string,
    endDate: Date,
    requestedBy: string,
  ) {
    return CampusClient.post<void>(
      `courseRegistration/dummy-unregister-student-from-class`,
      {
        courseClassId,
        userClientId: studentId,
        endDate,
        requestedBy,
      },
    )
  }
}

// Each status travels in ITS OWN field. The three were shifted by one (Completed
// -> removedEndReason, Failed -> completedEndReason, Removed -> failedEndReason).
// Harmless in practice — campus reads
// `removedEndReason || completedEndReason || failedEndReason`, so whichever field
// carried it resolved the same — but the payload contradicted the DTO's own
// @ValidateIf conditions, and only RemovedEndReasonDto declares `removedBy`. This
// aligns the wire shape with the contract; behaviour is unchanged.
function endReasonToRequest(endReason: CourseRegistrationEndReason) {
  switch (endReason.status) {
    case CourseRegistrationEndStatus.Completed:
      return {
        endStatus: CourseRegistrationEndStatus.Completed,
        completedEndReason: endReason,
      }
    case CourseRegistrationEndStatus.Failed:
      return {
        endStatus: CourseRegistrationEndStatus.Failed,
        failedEndReason: endReason,
      }
    case CourseRegistrationEndStatus.Removed:
      return {
        endStatus: CourseRegistrationEndStatus.Removed,
        removedEndReason: endReason,
      }
  }
}
